<!--
  Preloading strategy:
    - preference for KISSed unique small spinner for breadcrumber only
      rather than preloader placeholders
-->
<template>
  <div
    :class="[
      'page-list',
      'page-list--company-leave-request',
    ]"
  >
    <layout-teleport-to-breadcrumb>
      <layout-breadcrumb-loader
        :predicate="fetchState.status.value === 'success'"
      />
    </layout-teleport-to-breadcrumb>

    <transition
      v-if="fetchState.status.value === 'success'"
      appear
      appear-active-class="animate__animated animate__fadeIn ease-in-out a1"
    >
      <div class="page-list__body">

        <leave-request-filters-component
          class="page-list__filters"
          v-bind="{
            filters: leaveRequestFilters,
            updateFiltersCallback: updateLeaveRequestFiltersCallback,
            serviceList,
            parametersModule,
          }"
        />

        <pending-list
          ref="pendingList"
          v-bind="{
            approveCallback,
            approveMultipleCallback,
            rejectCallback,
            displayServiceName,
            daysCalculationType,
            anyFilterSet: anyLeaveRequestFilterSet,
            withBusyState: withPendingBusyState,
            viewProofDocumentCallback,
            importProofDocumentCallback,
            exportProofDocumentCallback,
            discardProofDocumentCallback,
            estimatedBalanceCallback,
            listCallback: searchPendingCallback,
            parametersModule,
          }"
          :default-data="defaultPendingData"
          :class="[
            'page-list__section',
            'page-list__section--pending-list'
          ]"
        >
          <template #actions>
            <ui-tooltip-wrapper
              :status="hasACL(ACLS.LEAVE_REQUEST_CREATE) ?
                UI.STATUS.DISABLED :
                UI.STATUS.ENABLED
              "
              v-bind="{
                title: `Vous n'avez pas les droits pour effectuer cette action`,
                variant: UI.VARIANT.LIGHT,
              }"
            >
              <layout-cta-sub
                component="ui-button"
                :status="
                  hasACL(
                    [
                      ACLS.LEAVE_REQUEST_CREATE,
                      ACLS.ABSENCE_REQUEST_CREATE
                    ]
                  ) ?
                    UI.STATUS.ENABLED :
                    UI.STATUS.DISABLED
                "
                :variant="UI.VARIANT.PRIMARY"
                :size="UI.SIZE.SMALL"
                @click="() => showLeaveRequestCreationSidebar = true"
              >
                <ui-icon
                  name="add"
                />
                &nbsp;
                Nouvelle demande
              </layout-cta-sub>
            </ui-tooltip-wrapper>
          </template>
        </pending-list>


        <archived-list
          ref="archivedList"
          v-bind="{
            cancelCallback,
            displayServiceName,
            daysCalculationType,
            anyFilterSet: anyLeaveRequestFilterSet,
            withBusyState: withArchivedBusyState,
            viewProofDocumentCallback,
            importProofDocumentCallback,
            exportProofDocumentCallback,
            discardProofDocumentCallback,
            listCallback: searchArchivedCallback,
            parametersModule,
          }"
          :default-data="defaultArchivedData"
          :class="[
            'page-list__section',
            'page-list__section--archived-list'
          ]"
        />

      </div>

    </transition>

    <!-- SIDEBAR: new leave request -->
    <ui-sidebar
      :id="TRANSAT_NEW_LEAVE_REQUEST_MODAL_NAME"
      v-model="showLeaveRequestCreationSidebar"
      placement="end"
      shadow
      no-close-on-esc
      lazy
      fit-workspace
    >
      <template #title>
        <ui-wad class="font-family-heading-jumbo-light font-size-24px">
          Ajout d'une nouvelle demande
        </ui-wad>
      </template>

      <leave-request-create
        v-bind="{
          singleMiscEstimateDayCountCallback,
          singleLeaveTypeEstimateDayCountCallback,
          bulkEstimateDayCountCallback,
          singleScheduleRecordCallback,
          submitSingleCallback: singleCreationCallback,
          submitSingleMiscLeaveCallback: singleCreationMiscLeaveCallback,
          bulkScheduleRecordCallback,
          submitBulkCallback: bulkCreationCallback,
          submitBulkMiscLeaveCallback: bulkCreationMiscLeaveCallback,
          serviceList,
          daysCalculationType,
          miscTypeList,
          parametersModule,
        }"
      />
    </ui-sidebar>

  </div>
</template>

<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/variables
@import ~/assets/sass/modules/transat/members

.page-list
  $self: &

  padding-top: $layout-spacer-y * .25
  padding-left: $layout-spacer-x
  padding-right: $layout-spacer-x
  padding-bottom: $layout-spacer-y

  &__filters
    margin-bottom: $layout-spacer-y * 2
    max-width: map-get($breakpoints, 'laptop')

  &__body
    // empirical
    // (due to a lot of data here, could not rely on `$breakpoints`)
    $min-width: 1240px

    min-width: $min-width

  &__section
    margin-top: $layout-spacer-y * 1.5

</style>

<style lang="sass">
@import '~/assets/sass/modules/layout/members'
@import '~/assets/sass/modules/transat/variables'

$simulated-header-height: 54px
$top-teaser-height: 16px
$background-color: $body-bg

// @XXX Vue3 lack of State-Driven Dynamic CSS API
#transat-leave-request-shortcut-creation-sidebar
  width: $create-sidebar-size
  background-color: $background-color

  .offcanvas-body

    &::after
      $offset-x: calc( var(--bs-offcanvas-padding-x) * -1 )

      @include virtual-content
      @include no-interaction
      @include top-teaser($background-color)

      position: fixed
      width: 100%
      height: $top-teaser-height
      top: $simulated-header-height // (quick & dirty teaser integration)
      transform: translateX($offset-x)

  @include media('<#{$create-sidebar-size}')
    @include fit-sidebar($create-sidebar-size)

</style>

<script lang="ts">
import LeaveRequestCreate from '~/components/transat/leave-request/create/Organism.vue'
import LeaveRequestFiltersComponent from '~/components/transat/leave-request/list/Filters.vue'
import ArchivedList, { type DefaultData as ArchivedListDefaultData } from '~/components/transat/leave-request/list/ArchivedList.vue'
import PendingList, { type DefaultData as PendingListDefaultData} from '~/components/transat/leave-request/list/PendingList.vue'
import Console from '~/utils/lib/core/console'
import { clone, delay, merge, sustainedPromise, tapCatch, toString } from '~/utils/lib/core/prelude'
import { type DAYS_CALCULATION } from '~/stores/transat/leave-request'
import { anyFilterSet, EVENTS, leaveRequestBoilerplate, useStore_transat_leaveRequest_list, type LeaveRequestFilters } from '~/stores/transat/leave-request/list'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useToastBuilder } from '~/composables/layout/toast'
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation'
import { useAccessControl } from '~/composables/layout/access-control'
import { INITIAL_HYDRATION, type Context } from '~/composables/layout/table-provider'
import { wrap } from '~/utils/infra/common/types'
import { useProofFeatures } from '~/components/transat/leave-request/summary/proof.composable'
import { useStore_transat_leaveRequest_misc, type MiscLeaveRule } from '~/stores/transat/leave-request/misc'
import { useStore_transat_service_list } from '~/stores/transat/service/list'
import { useStore_transat_leaveRequest_details } from '~/stores/transat/leave-request/details'
import { useStore_transat_leaveRequest_create } from '~/stores/transat/leave-request/create'
import type { Service_ } from '~/stores/transat/service'
import { useStore_transat_leaveRequest_createBulk } from '~/stores/transat/leave-request/create-bulk'
import { useStore_transat_parameters_module, type LeaveModuleInterface } from '~/stores/transat/parameters/module'
import { useStore_transat_layout } from '~/stores/transat/layout'

const TRANSAT_NEW_LEAVE_REQUEST_MODAL_NAME = 'transat-leave-request-shortcut-creation-sidebar'

const console = new Console('component:leave-request-list')

export default defineComponent({
  components: { ArchivedList, PendingList, LeaveRequestFiltersComponent, LeaveRequestCreate },
  provide() {
    return {
      [EVENTS.ON_ARCHIVED_LIST_REFRESH_NEEDED]:
        (muteBusyState) => this.refreshArchivedList(muteBusyState),
      [EVENTS.ON_PENDING_LIST_REFRESH_NEEDED]:
        (muteBusyState) => this.refreshPendingList(muteBusyState),
    }
  },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
      menuItem: MENU_ITEM.ABSENCES,
    })
    const navigationContext = useCompanyRequiredNavigationContext()

    useHead({
      title: `Demandes de congés — ${navigationContext.fromCompanyLevel().company.raison_sociale}`,
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const ToastBuilder = useToastBuilder()

    const ref_archivedList
      = useTemplateRef<InstanceType<typeof ArchivedList>>('archivedList')

    const ref_pendingList
      = useTemplateRef<InstanceType<typeof PendingList>>('pendingList')

    const {
      viewProofDocumentCallback,
      importProofDocumentCallback,
      exportProofDocumentCallback,
      discardProofDocumentCallback,
    } = useProofFeatures()

    const store_transat_layout = useStore_transat_layout()
    const store_service_list = useStore_transat_service_list()
    const store_leaveRequest_list = useStore_transat_leaveRequest_list()
    const store_leaveRequest_misc = useStore_transat_leaveRequest_misc()
    const store_leaveRequest_details = useStore_transat_leaveRequest_details()
    const store_leaveRequest_create = useStore_transat_leaveRequest_create()
    const store_leaveRequest_createBulk = useStore_transat_leaveRequest_createBulk()
    const store_parameters_module = useStore_transat_parameters_module()

    store_transat_layout.setNavSection('list_leave')

    return {
      ...navigationContext,
      ...useAccessControl(),
      fetchState,
      fetchWrapper,
      ToastBuilder,
      ref_archivedList,
      ref_pendingList,
      viewProofDocumentCallback,
      importProofDocumentCallback,
      exportProofDocumentCallback,
      discardProofDocumentCallback,
      store_service_list,
      store_leaveRequest_list,
      store_leaveRequest_misc,
      store_leaveRequest_details,
      store_leaveRequest_create,
      store_leaveRequest_createBulk,
      store_parameters_module,
    }
  },
  data() {
    const leaveRequestFilters = clone(leaveRequestBoilerplate)

    return {
      leaveRequestFilters,
      defaultPendingData: null as unknown as PendingListDefaultData, // (lazy)
      defaultArchivedData: null as unknown as ArchivedListDefaultData, // (lazy)
      displayServiceName: null as unknown as boolean, // (lazy)
      daysCalculationType: null as unknown as DAYS_CALCULATION, // (lazy)
      parametersModule: null as unknown as LeaveModuleInterface, // (lazy)
      withPendingBusyState: true,
      withArchivedBusyState: true,
      serviceList: [] as Service_[], // (lazy)
      miscTypeList: [] as MiscLeaveRule[], // (lazy)
      /// Creation
      TRANSAT_NEW_LEAVE_REQUEST_MODAL_NAME,
      showLeaveRequestCreationSidebar: false,
    }
  },
  computed: {
    anyLeaveRequestFilterSet() {
      return anyFilterSet(this.leaveRequestFilters)
    },
  },
  created() {
    this.fetchWrapper(
      () => {
        const options = {
          uuidData: this.fromCompanyLevel().structure.uuidData,
          code_societe: this.fromCompanyLevel().company.code_societe
        }

        const defaultPendingContext :Partial<Context> = {
          perPage: 20,
          currentPage: 1,
          order: 'from',
          sort: 'asc',
        }

        const defaultArchivedContext :Partial<Context>= {
          perPage: 20,
          currentPage: 1,
          order: 'from',
          sort: 'desc',
        }

        return Promise.all([
            this.searchPendingCallback(defaultPendingContext),
            this.searchArchivedCallback(defaultArchivedContext),
            this.store_service_list.readEntire(options),
            this.store_leaveRequest_misc.read_(options),
            this.store_parameters_module.read(options)
          ])
          .then(results => {

            this.defaultPendingData = {
              initialHydration: INITIAL_HYDRATION.EAGER_INJECTION,
              currentItems: results[0].data,
              context: merge(
                defaultPendingContext,
                results[0].context
              )
            }

            this.defaultArchivedData = {
              initialHydration: INITIAL_HYDRATION.EAGER_INJECTION,
              currentItems: results[1].data,
              context: merge(
                defaultArchivedContext,
                results[1].context,
              )
            }

            this.displayServiceName = results[0].show_team_names

            this.daysCalculationType = results[0].mode_decompte

            this.serviceList = results[2]

            this.miscTypeList = results[3]

            this.parametersModule = results[4]
          })
          .catch(tapCatch((err) => {
            console.warn('fetch', err)

            this.ToastBuilder
              .error(toString(err))
              .title(`Demande de congés`)
          }))
      }
    )
  },
  methods: {
    updateLeaveRequestFiltersCallback(
      leaveRequestFilters :LeaveRequestFilters
    ) {
      this.leaveRequestFilters = leaveRequestFilters

      const muteBusyState = false

      this.refreshArchivedList(muteBusyState)
      this.refreshPendingList(muteBusyState)
    },
    searchPendingCallback(context) {
      const input = {
        ...wrap(context),
        filters: clone(this.leaveRequestFilters)
      }

      const options = {
        input,
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      return this.store_leaveRequest_list.readPending(options)
        .catch(tapCatch(err => {
          console.warn('searchPendingCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Recherche de demandes en attente`)
            .show()
        }))
        .finally(() => this.withPendingBusyState = true)
    },
    searchArchivedCallback(context) {
      const input = {
        ...wrap(context),
        filters: clone(this.leaveRequestFilters)
      }

      const options = {
        input,
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      return this.store_leaveRequest_list.readArchived(options)
        .catch(tapCatch(err => {
          console.warn('searchArchivedCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Recherche de demandes archivées`)
            .show()
        }))
        .finally(() => this.withArchivedBusyState = true)
    },
    approveCallback(grouped_leave_id, reason = undefined) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        grouped_leave_id,
        reason
      }

      return this.store_leaveRequest_details.approve(options)
        .catch(tapCatch(err => {
          console.warn('approveCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Validation d'une demande`)
            .show()
        }))
    },
    approveMultipleCallback(data) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        data
      }

      return this.store_leaveRequest_details.approveMultiple(options)
        .catch(tapCatch(err => {
          console.warn('approveMultipleCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Validation groupée de demandes`)
            .show()
        }))
    },
    cancelCallback(grouped_leave_id, reason) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        grouped_leave_id,
        reason
      }

      return this.store_leaveRequest_details.cancel(options)
        .catch(tapCatch(err => {
          console.warn('cancelCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Annulation d'une demande`)
            .show()
        }))
    },
    rejectCallback(grouped_leave_id, reason) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        grouped_leave_id,
        reason
      }

      return this.store_leaveRequest_details.reject(options)
        .catch(tapCatch(err => {
          console.warn('rejectCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Refus d'une demande`)
            .show()
        }))
    },
    singleScheduleRecordCallback({ matricule, fromDate, toDate, leaveType_, miscLeaveRuleId }) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        dateMin: fromDate,
        dateMax: toDate,
        matricule,
        leaveType_Context: leaveType_,
        miscLeaveRuleId,
      }

      return this.store_leaveRequest_create.readSchedulingRecord(options)
        .catch(tapCatch(err => {
          console.warn('singleScheduleRecordCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Récupération des congés d'un utilisateur`)
            .show()
        }))
    },
    bulkScheduleRecordCallback({ fromDate, toDate, leaveType_, miscLeaveRuleId }) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        dateMin: fromDate,
        dateMax: toDate,
        leaveType_Context: leaveType_,
        miscLeaveRuleId,
      }

      return this.store_leaveRequest_createBulk.readSchedulingRecord(options)
        .catch(tapCatch(err => {
          console.warn('bulkScheduleRecordCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Récupération du calendrier`)
            .show()
        }))
    },
    singleMiscEstimateDayCountCallback(matricule, data) {
      const options = {
        matricule,
        data,
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      return this.store_leaveRequest_create.miscEstimateDayCount(options)
        .catch(tapCatch(err => {
          console.warn('singleMiscEstimateDayCountCallback', err)
        }))
    },
    singleLeaveTypeEstimateDayCountCallback(matricule, data) {
      const options = {
        matricule,
        data,
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      return this.store_leaveRequest_create.leaveTypeEstimateDayCount(options)
        .catch(tapCatch(err => {
          console.warn('singleLeaveTypeEstimateDayCountCallback', err)
        }))
    },
    bulkEstimateDayCountCallback(data) {
      const options = {
        data,
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      return this.store_leaveRequest_createBulk.estimateDayCount(options)
        .catch(tapCatch(err => {
          console.warn('bulkEstimateDayCountCallback', err)
        }))
    },
    singleCreationCallback(data) {
      const options = {
        data,
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      return sustainedPromise(
          300,
          this.store_leaveRequest_create.exec(options),
        )
        .then(async () => {
          this.showLeaveRequestCreationSidebar = false

          this.refreshArchivedList()

          // (defensive waiting preventing overload stimulation before next IO)
          await delay(300)

          this.ToastBuilder
            .success()
            .title(`Demande de congés ajoutée et validée`)
            .show()
        })
        .catch(tapCatch(err => {
          console.warn('singleCreationCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Ajout d'une demande`)
            .show()
        }))
    },
    bulkCreationCallback(data) {
      const options = {
        data,
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      return sustainedPromise(
          300,
          this.store_leaveRequest_createBulk.exec(options),
        )
        .then(async () => {
          this.showLeaveRequestCreationSidebar = false

          this.refreshArchivedList()

          // (defensive waiting preventing overload stimulation before next IO)
          await delay(300)

          this.ToastBuilder
            .success()
            .title(`Demandes de congés ajoutées et validées`)
            .show()
        })
        .catch(tapCatch(err => {
          console.warn('bulkCreationCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Ajout de demandes groupées`)
            .show()
        }))
    },
    singleCreationMiscLeaveCallback(data) {
      const options = {
        data,
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      return sustainedPromise(
          300,
          this.store_leaveRequest_create.execForMiscLeave(options),
        )
        .then(async () => {
          this.showLeaveRequestCreationSidebar = false

          this.refreshArchivedList()

          // (defensive waiting preventing overload stimulation before next IO)
          await delay(300)

          this.ToastBuilder
            .success()
            .title(`Demande de congés ajoutée et validée`)
            .show()
        })
        .catch(tapCatch(err => {
          console.warn('singleCreationMiscLeaveCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Ajout d'une demande`)
            .show()
        }))
    },
    bulkCreationMiscLeaveCallback(data) {
      const options = {
        data,
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      return sustainedPromise(
          300,
          this.store_leaveRequest_createBulk.execForMiscLeave(options),
        )
        .then(async () => {
          this.showLeaveRequestCreationSidebar = false

          this.refreshArchivedList()

          // (defensive waiting preventing overload stimulation before next IO)
          await delay(300)

          this.ToastBuilder
            .success()
            .title(`Demandes de congés ajoutées et validées`)
            .show()
        })
        .catch(tapCatch(err => {
          console.warn('bulkCreationMiscLeaveCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Ajout de demandes groupées`)
            .show()
        }))
    },
    estimatedBalanceCallback({ matricule, grouped_leave_id }) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        grouped_leave_id,
        matricule
      }

      return this.store_leaveRequest_details.estimatedBalance(options)
        .catch(tapCatch(err => {
          console.warn('estimatedBalanceCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Estimation du solde`)
            .show()
        }))
    },
    refreshArchivedList(muteBusyState = false) {
      if (muteBusyState)
        this.withArchivedBusyState = false

      this.ref_archivedList?.refreshTable()
    },
    refreshPendingList(muteBusyState = false) {
      if (muteBusyState)
        this.withPendingBusyState = false

      this.ref_pendingList?.refreshTable()
    },
  }
})
</script>
